import { Crown } from "lucide-react";
import { Spinner, ProgressBar } from "../../shared/ui";

// ── Quiplash Host Screen ──────────────────────────────────────────────────────
// Props:
//   prompt      : string | null
//   answerCount : number
//   answerTotal : number
//   voteData    : { prompt, answers } | null
//   voteCount   : number
//   voteTotal   : number
//   result      : object | null  — { prompt, answers, vote_counts, scores, skipped }
//   players     : object[]
//   tv          : bool

export default function QuiplashHost({ prompt, answerCount, answerTotal, voteData, voteCount, voteTotal, result, players, tv, onSkip }) {

  // Phase 1: collecting answers
  if (!voteData && !result) {
    return (
      <div className="w-full text-center animate-slide-up">
        <div style={{
          display: "inline-block", background: "#001f33", border: "3px solid #000",
          borderRadius: "1.5rem", boxShadow: "5px 5px 0 #000",
          padding: tv ? "1.75rem 2.75rem" : "0.9rem 1.25rem", maxWidth: "91%", marginBottom: "1.5rem",
        }}>
          <div className="font-heading text-[#00DDFF] mb-2" style={{ fontSize: tv ? "1rem" : "0.75rem", opacity: 0.7 }}>
            💬 Закончи фразу
          </div>
          <h2 className="font-heading text-white" style={{ fontSize: tv ? "2.5rem" : "1.35rem", lineHeight: 1.3 }}>
            {prompt || "..."}
          </h2>
        </div>
        <div className="font-heading text-[#aaa] mb-3" style={{ fontSize: tv ? "1.35rem" : "0.9rem" }}>
          Ответили: {answerCount}/{answerTotal}
        </div>
        <ProgressBar value={answerCount} max={Math.max(answerTotal, 1)} color="#00DDFF" tv={tv} />
        <div className="flex flex-wrap justify-center gap-3 mt-5">
          {players.map((p, i) => (
            <div key={i} className="player-chip" style={{
              backgroundColor: p.color,
              fontSize: tv ? "1.35rem" : "0.9rem",
              padding: tv ? "0.5rem 1.3rem" : "0.35rem 0.9rem",
            }}>
              {p.name}
            </div>
          ))}
        </div>
        {answerCount > 0 && (
          <button onClick={onSkip} className="btn-cartoon btn-primary" style={{ marginTop: "1.25rem", fontSize: tv ? "1.1rem" : "0.85rem", padding: tv ? "0.6rem 1.75rem" : "0.4rem 1.25rem" }}>
            Показать ответы →
          </button>
        )}
      </div>
    );
  }

  // Phase 2: voting — show all answers anonymously, players vote on phones
  if (voteData && !result) {
    const entries = Object.entries(voteData.answers);
    return (
      <div className="w-full text-center animate-slide-up">
        <div style={{
          display: "inline-block", background: "#001f33", border: "3px solid #000",
          borderRadius: "1.5rem", boxShadow: "5px 5px 0 #000",
          padding: tv ? "1.25rem 2.5rem" : "0.75rem 1rem", maxWidth: "91%", marginBottom: "1.25rem",
        }}>
          <h2 className="font-heading text-white" style={{ fontSize: tv ? "2rem" : "1.1rem", lineHeight: 1.3 }}>
            {voteData.prompt}
          </h2>
        </div>
        <div className="font-heading text-[#aaa] mb-3" style={{ fontSize: tv ? "1.1rem" : "0.8rem" }}>
          🗳️ Голосуют: {voteCount}/{voteTotal}
        </div>
        <div style={{ maxWidth: tv ? "700px" : "100%", margin: "0 auto", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {entries.map(([author, text], i) => (
            <div key={author} style={{
              background: "#1a1030", border: "2.5px solid #000", borderRadius: "0.85rem",
              boxShadow: "3px 3px 0 #000", padding: tv ? "0.9rem 1.25rem" : "0.6rem 0.9rem",
              display: "flex", alignItems: "center", gap: "0.75rem", textAlign: "left",
            }}>
              <span className="font-display" style={{ color: "#00DDFF", fontSize: tv ? "1.5rem" : "1rem", minWidth: "1.5rem" }}>
                {i + 1}
              </span>
              <span className="font-heading text-white" style={{ fontSize: tv ? "1.4rem" : "0.95rem", flex: 1 }}>
                {text}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Phase 3: results — reveal who wrote what and vote counts
  if (result) {
    const entries = Object.entries(result.answers || {});
    const vc = result.vote_counts || {};
    const sorted = [...entries].sort((a, b) => (vc[b[0]] || 0) - (vc[a[0]] || 0));
    return (
      <div className="w-full text-center animate-slide-up">
        <div style={{
          display: "inline-block", background: "#001f33", border: "3px solid #000",
          borderRadius: "1.5rem", boxShadow: "5px 5px 0 #000",
          padding: tv ? "1rem 2.25rem" : "0.65rem 0.9rem", maxWidth: "91%", marginBottom: "1.25rem",
        }}>
          <h2 className="font-heading text-white" style={{ fontSize: tv ? "1.75rem" : "0.95rem", lineHeight: 1.3 }}>
            {result.prompt}
          </h2>
        </div>
        {result.skipped ? (
          <p className="font-heading text-[#aaa]">Недостаточно ответов для голосования</p>
        ) : (
          <div style={{ maxWidth: tv ? "720px" : "100%", margin: "0 auto", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {sorted.map(([author, text], i) => {
              const pl = players.find(p => p.name === author);
              const votes = vc[author] || 0;
              const isWinner = i === 0 && votes > 0;
              return (
                <div key={author} style={{
                  background: "#1a1030",
                  border: `2.5px solid ${isWinner ? "#ffe600" : "#000"}`,
                  borderRadius: "0.85rem",
                  boxShadow: isWinner ? "3px 3px 0 #ffe600" : "3px 3px 0 #000",
                  padding: tv ? "0.9rem 1.25rem" : "0.55rem 0.85rem",
                  display: "flex", alignItems: "center", gap: "0.75rem", textAlign: "left",
                }}>
                  {isWinner && <Crown style={{ color: "#ffe600", width: tv ? "1.5rem" : "1rem", height: tv ? "1.5rem" : "1rem", flexShrink: 0 }} />}
                  <div style={{ flex: 1 }}>
                    <div className="font-heading text-white" style={{ fontSize: tv ? "1.3rem" : "0.9rem" }}>{text}</div>
                    <div className="font-heading" style={{ fontSize: tv ? "1rem" : "0.75rem", color: pl?.color || "#aaa", marginTop: "0.15rem" }}>
                      — {author}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    <span className="font-display" style={{ fontSize: tv ? "1.75rem" : "1.1rem", color: votes > 0 ? "#ffe600" : "#555" }}>
                      {votes}
                    </span>
                    <span className="font-heading text-[#555]" style={{ fontSize: tv ? "0.85rem" : "0.65rem" }}>голос.</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return <Spinner color="#00DDFF" tv={tv} />;
}